// ======================= JOB SCHEDULE API =======================

import { apiCall } from "../base";
import { HttpMethod } from "@/types/https";
import { ENDPOINT } from "../endpoint";
import {
  JobScheduleResponse,
  TechnicianScheduleResponse,
  MachineScheduleResponse,
  JobScheduleQueryParams,
  AskAiPlanningPayload,
  AskAiPlanningResponse,
} from "../types/job-schedule.type";

/**
 * Get job-centric schedule data
 * Shows schedule from job perspective with all technician/machine assignments
 */
export const GetJobScheduleByJob = (params: JobScheduleQueryParams) => ({
  name: `JobSchedule_${params.workspaceId}_${params.from || ""}_${params.to || ""}_${params.search || ""}_${params.filters || ""}_${params.sorts || ""}`,
  handle: async (): Promise<JobScheduleResponse> => {
    return apiCall<JobScheduleResponse>({
      url: ENDPOINT.JOB_SCHEDULE_JOBS,
      method: HttpMethod.GET,
      params: params, // Let apiCall handle query string conversion
    });
  },
});

/**
 * Get job planning data (all workflows, including unscheduled rows)
 */
export const GetJobSchedulePlanningByJob = (params: JobScheduleQueryParams) => ({
  name: `JobSchedulePlanning_${params.workspaceId}_${params.search || ""}_${params.filters || ""}_${params.sorts || ""}`,
  handle: async (): Promise<JobScheduleResponse> => {
    return apiCall<JobScheduleResponse>({
      url: ENDPOINT.JOB_SCHEDULE_JOBS_PLANNING,
      method: HttpMethod.GET,
      params: params,
    });
  },
});

/**
 * Ask AI to generate workflow planning recommendations.
 */
export const AskAiJobSchedulePlanning = () => ({
  name: "AskAiJobSchedulePlanning",
  handle: async (payload: AskAiPlanningPayload): Promise<AskAiPlanningResponse> => {
    return apiCall<AskAiPlanningResponse>({
      url: ENDPOINT.JOB_SCHEDULE_JOBS_PLANNING_ASK_AI,
      method: HttpMethod.POST,
      body: payload,
    });
  },
});

/**
 * Get technician-centric schedule data
 * Shows schedule grouped by technicians with their job assignments
 */
export const GetJobScheduleByTechnician = (params: JobScheduleQueryParams) => ({
  name: `TechnicianSchedule_${params.workspaceId}_${params.from || ""}_${params.to || ""}_${params.search || ""}_${params.filters || ""}_${params.sorts || ""}`,
  handle: async (): Promise<TechnicianScheduleResponse> => {
    return apiCall<TechnicianScheduleResponse>({
      url: ENDPOINT.JOB_SCHEDULE_TECHNICIANS,
      method: HttpMethod.GET,
      params: params, // Let apiCall handle query string conversion
    });
  },
});

/**
 * Get machine-centric schedule data
 * Shows schedule grouped by machines with their job assignments
 */
export const GetJobScheduleByMachine = (params: JobScheduleQueryParams) => ({
  name: `MachineSchedule_${params.workspaceId}_${params.from || ""}_${params.to || ""}_${params.search || ""}_${params.filters || ""}_${params.sorts || ""}`,
  handle: async (): Promise<MachineScheduleResponse> => {
    return apiCall<MachineScheduleResponse>({
      url: ENDPOINT.JOB_SCHEDULE_MACHINES,
      method: HttpMethod.GET,
      params: params, // Let apiCall handle query string conversion
    });
  },
});
